import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  AccountInfo,
  Break,
  ExternalFileFilter,
  JobsPayload,
  SecurityMappingFilter,
} from './focused.models'

export type FocusedRedux = {
  securityIds: number[]
  break?: Break
  accountIds: number[]
  /**
   * DLITE-4110: the single account whose comments the drawer should show, set
   * by selecting a row in the Accounts panel. Null when the current selection
   * doesn't map to a commentary-eligible account (nothing / multiple rows /
   * an aggregate). Distinct from `accountIds`, which tracks every selected row
   * for highlighting and break sums.
   */
  commentaryAccountId?: number | null
  subSleeveAccountId?: number | null
  accountInfo: AccountInfo[]
  totalAccountBreaks?: number
  positionSecurityId?: number
  assetId?: string
  cwTranId?: number //legacyTranId, not the new UUID
  extTranId?: number //legacyTranId, not the new UUID
  cwTranIds: string[]
  extTranIds: number[]
  corporateActionIssueId?: string
  uiComponentName?: string // focused panel, popup, etc. Major elements of the app which we will call a page for the user analytics tools
  uiComponentKey?: string
  externalFileFilter?: ExternalFileFilter
  securityMappingFilter?: SecurityMappingFilter
  bulkCwTranIds?: number[]
  bulkExtTranIds?: number[]
  legacyTranIds: number[]
  legacyOrigIds: number[]
  extPosId?: number
  jobs?: JobsPayload
  approvalAccountIds?: number[]
}

export const focusedInitialState: FocusedRedux = {
  securityIds: [],
  break: undefined,
  accountIds: [],
  commentaryAccountId: null,
  subSleeveAccountId: null,
  accountInfo: [],
  totalAccountBreaks: undefined,
  positionSecurityId: undefined,
  assetId: undefined,
  cwTranId: undefined,
  extTranId: undefined,
  cwTranIds: [],
  extTranIds: [],
  corporateActionIssueId: undefined,
  externalFileFilter: undefined,
  securityMappingFilter: undefined,
  bulkCwTranIds: undefined,
  bulkExtTranIds: undefined,
  legacyTranIds: [],
  legacyOrigIds: [],
  extPosId: undefined,
  jobs: undefined,
  approvalAccountIds: [],
}

export const focusedFeatureKey = 'focused'

export const focusedSlice = createSlice({
  name: focusedFeatureKey,
  reducers: {
    setFocusedAccounts: (
      state,
      action: PayloadAction<{ accountIds: number[] }>,
    ) => ({ ...state, accountIds: action.payload.accountIds }),
    setFocusedAccountInfo: (
      state,
      action: PayloadAction<{ accountInfo: AccountInfo[] }>,
    ) => ({ ...state, accountInfo: action.payload.accountInfo }),
    // DLITE-4110: set/clear the account that drives the comments drawer from
    // Accounts-panel selection (independent of loading a session).
    setFocusedCommentaryAccountId: (
      state,
      action: PayloadAction<{ accountId: number | null }>,
    ) => ({ ...state, commentaryAccountId: action.payload.accountId }),
    setFocusedTotalAccountBreaks: (
      state,
      action: PayloadAction<{ totalBreaks: number }>,
    ) => ({
      ...state,
      totalAccountBreaks: action.payload.totalBreaks,
    }),

    setFocusedBreak: (state, action: PayloadAction<Break | undefined>) => ({
      ...state,
      break: action.payload,
    }),

    setFocusedCorporateActionIssue: (
      state,
      action: PayloadAction<string | undefined>,
    ) => ({
      ...state,
      corporateActionIssueId: action.payload,
    }),

    setFocusedCWTransaction: (
      state,
      action: PayloadAction<number | undefined>,
    ) => ({
      ...state,
      cwTranId: action.payload,
    }),

    setFocusedCWTransactions: (state, action: PayloadAction<string[]>) => ({
      ...state,
      cwTranIds: [...action.payload],
    }),

    clearFocusedCWTransaction: state => ({ ...state, cwTranId: undefined }),

    setFocusedExtTransaction: (
      state,
      action: PayloadAction<number | undefined>,
    ) => ({
      ...state,
      extTranId: action.payload,
    }),

    setFocusedExtTransactions: (state, action: PayloadAction<number[]>) => ({
      ...state,
      extTranIds: [...action.payload],
    }),

    clearFocusedExtTranId: state => ({ ...state, extTranId: undefined }),

    setFocusedPositionSecurity: (
      state,
      action: PayloadAction<number | undefined>,
    ) => ({
      ...state,
      positionSecurityId: action.payload,
    }),

    clearFocusedPositionSecurity: state => ({
      ...state,
      positionSecurityId: undefined,
    }),

    setFocusedSecurities: (
      state,
      action: PayloadAction<{ securityIds: number[] }>,
    ) => ({ ...state, securityIds: action.payload.securityIds }),

    setFocusedSubSleeveAccountId: (
      state,
      action: PayloadAction<{ subSleeveAccountId: number | undefined }>,
    ) => ({
      ...state,
      subSleeveAccountId: action.payload.subSleeveAccountId
        ? action.payload.subSleeveAccountId
        : null,
    }),

    setFocusedAssetId: (state, action: PayloadAction<string | undefined>) => ({
      ...state,
      assetId: action.payload,
    }),

    setFocusedBulkCwTransactions: (
      state,
      action: PayloadAction<{ tranIds: number[] }>,
    ) => ({
      ...state,
      bulkCwTranIds: action.payload.tranIds,
    }),

    setFocusedBulkExtTransactions: (
      state,
      action: PayloadAction<{ tranIds: number[] }>,
    ) => ({
      ...state,
      bulkExtTranIds: action.payload.tranIds,
    }),

    setFocusedLegacyTranIds: (state, action: PayloadAction<number[]>) => ({
      ...state,
      legacyTranIds: [...action.payload],
    }),

    setFocusedLegacyOrigIds: (state, action: PayloadAction<number[]>) => ({
      ...state,
      legacyOrigIds: [...action.payload],
    }),

    setFocusedExtPosId: (state, action: PayloadAction<number | undefined>) => ({
      ...state,
      extPosId: action.payload,
    }),

    clearFocusedExtPosId: state => ({ ...state, extPosId: undefined }),

    setFocusedApprovalsAccountId: (
      state,
      action: PayloadAction<number[] | undefined>,
    ) => ({
      ...state,
      approvalAccountIds: action.payload,
    }),

    clearFocusedApprovalsAccountId: state => ({
      ...state,
      approvalAccountIds: undefined,
    }),

    /**
     * Sets the currently-focused UI component (panel, modal, etc), for purposes of usage analytics
     *
     * @param state - redux state
     * @param action - Payload is name of the component
     */
    setFocusedUIComponent: (
      state,
      action: PayloadAction<{ name: string; key: string }>,
    ) => ({
      ...state,
      uiComponentName: action.payload.name,
      uiComponentKey: action.payload.key,
    }),

    setFocusedExternalFileFilter: (
      state,
      action: PayloadAction<ExternalFileFilter | undefined>,
    ) => ({
      ...state,
      externalFileFilter: action.payload,
    }),

    setFocusedJobs: (
      state,
      action: PayloadAction<JobsPayload | undefined>,
    ) => ({
      ...state,
      jobs: action.payload,
    }),

    setFocusedSecurityMappingFilter: (
      state,
      action: PayloadAction<SecurityMappingFilter | undefined>,
    ) => ({
      ...state,
      securityMappingFilter: action.payload,
    }),

    clearFocusedSecurityMappingFilter: state => ({
      ...state,
      securityMappingFilter: undefined,
    }),
    clearFocusedJobs: state => ({
      ...state,
      jobs: undefined,
    }),

    clearFocused: state => ({ ...state, ...focusedInitialState }),
  },
  initialState: focusedInitialState,
})

export const {
  setFocusedAccounts,
  setFocusedAccountInfo,
  setFocusedCommentaryAccountId,
  setFocusedSubSleeveAccountId,
  setFocusedTotalAccountBreaks,
  setFocusedBreak,
  setFocusedCorporateActionIssue,
  setFocusedCWTransaction,
  setFocusedCWTransactions,
  setFocusedExtTransaction,
  setFocusedExtTransactions,
  setFocusedPositionSecurity,
  setFocusedSecurities,
  setFocusedAssetId,
  setFocusedBulkCwTransactions,
  setFocusedBulkExtTransactions,
  setFocusedLegacyTranIds,
  setFocusedLegacyOrigIds,
  setFocusedUIComponent,
  setFocusedExternalFileFilter,
  setFocusedSecurityMappingFilter,
  setFocusedExtPosId,
  setFocusedJobs,
  setFocusedApprovalsAccountId,
  clearFocused,
  clearFocusedPositionSecurity,
  clearFocusedCWTransaction,
  clearFocusedExtTranId,
  clearFocusedExtPosId,
  clearFocusedSecurityMappingFilter,
  clearFocusedJobs,
  clearFocusedApprovalsAccountId,
} = focusedSlice.actions

export default focusedSlice.reducer
